import { useEffect, useRef } from 'react'
import { useAuth } from '../auth/AuthContext'
import { useTheme, type ThemeChoice } from '../theme/ThemeProvider'
import { apiClient } from '../services/apiClient'
import { tokenStore } from '../auth/tokenStore'

/**
 * Sincroniza preferencias del usuario (tema + menú anclado) con el backend
 * vía PATCH /api/me/preferences cuando cambian.
 *
 * Sync rules:
 *   - Solo intenta sincronizar si hay sesión activa.
 *   - Debounce de 600ms para evitar flood al alternar rápidamente.
 *   - Errores se silencian (loguean en consola) — preferencias locales siguen funcionando.
 *   - Al montar con sesión, alinea el tema local con el del usuario remoto (one-way sync).
 */

type Patch = { theme?: ThemeChoice; menu_pinned?: boolean }

export function usePreferencesSync({ menuPinned }: { menuPinned: boolean }): void {
  const { state } = useAuth()
  const { choice, setChoice } = useTheme()

  // Al primer render con sesión: alinear el tema local al del usuario remoto.
  const alignedRef = useRef(false)
  useEffect(() => {
    if (alignedRef.current) return
    if (state.status !== 'authenticated') return
    alignedRef.current = true
    if (state.user.theme !== choice) {
      setChoice(state.user.theme)
    }
  }, [state, choice, setChoice])

  // Push de cambios al backend (debounced).
  const lastSentRef = useRef<Patch>({})
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (state.status !== 'authenticated') return

    const patch: Patch = {}
    if (state.user.theme !== choice) patch.theme = choice
    if (state.user.menu_pinned !== menuPinned) patch.menu_pinned = menuPinned

    if (Object.keys(patch).length === 0) return

    // Evitar duplicar el mismo patch consecutivo
    if (
      patch.theme === lastSentRef.current.theme &&
      patch.menu_pinned === lastSentRef.current.menu_pinned
    ) {
      return
    }

    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(async () => {
      try {
        await apiClient.patch<void>('/me/preferences', patch)
        lastSentRef.current = patch
        // Actualizar el user en localStorage para que el reload sea consistente.
        if (state.status === 'authenticated') {
          const updated = { ...state.user, ...patch }
          tokenStore.setUser(updated)
        }
      } catch (err) {
        console.warn('No se pudo sincronizar preferencias', err)
      }
    }, 600)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [state, choice, menuPinned])
}
